import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as formulationsApi from '../api/formulationsApi';
import { EmptyState, ErrorBanner, LoadMore, Modal, TableSkeleton } from './ui';
import { FormulationIO } from './FormulationDetail';
import { PaymentBadge, StatusBadge } from './StatusBadge';
import { IconChevronDown } from './icons';
import { ConvertedOrder } from '../types/formulation';
import { formatDateTime, formatINR, shortId } from '../utils/format';

/**
 * The orders behind "Placed an order" on the formulation funnel: every paid
 * made-to-order perfume, with the formula it was blended from.
 *
 * The formula is resolved by the gateway and can come up empty on old lines
 * that carry no link back to one — those rows still belong in the list, they
 * just expand to a note saying so.
 */
export function ConvertedOrdersModal({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<ConvertedOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await formulationsApi.getConvertedOrders({ page });
      // Page 1 replaces (first load and retry); later pages append.
      setRows((prev) => (page === 1 ? data.orders : [...prev, ...data.orders]));
      setTotal(data.pagination.total);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Modal
      open
      title={`Users who placed an order${total ? ` — ${total}` : ''}`}
      onClose={onClose}
      size="xl"
      bodyClassName=""
    >
      {error ? (
        <div className="p-5">
          <ErrorBanner message={error} onRetry={load} />
        </div>
      ) : loading && rows.length === 0 ? (
        <TableSkeleton rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          message="No formula has been bought yet"
          hint="Everyone who generated one stopped before paying"
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">User Name</th>
                  <th className="th">Email</th>
                  <th className="th">Order ID</th>
                  <th className="th">Amount</th>
                  <th className="th">Cluster</th>
                  <th className="th">Ordered At</th>
                  <th className="th">Status</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {rows.map((o) => {
                  const open = openId === o.order_id;
                  return [
                    <tr
                      key={o.order_id}
                      onClick={() => setOpenId(open ? null : o.order_id)}
                      className="cursor-pointer transition-colors hover:bg-edge/20"
                    >
                      <td className="td">
                        {/* stopPropagation on both links: leaving the modal
                            should not also toggle the row behind it. */}
                        <Link
                          to={`/users/${o.user_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-gold hover:underline"
                        >
                          {o.user_name?.trim() || 'Unnamed'}
                        </Link>
                      </td>
                      <td className="td text-sub">{o.user_email || '—'}</td>
                      <td className="td">
                        <Link
                          to={`/orders/${o.order_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-gold hover:underline"
                        >
                          {shortId(o.order_id)}
                        </Link>
                      </td>
                      <td className="td font-medium">{formatINR(o.total)}</td>
                      <td className="td">{o.cluster_label || '—'}</td>
                      <td className="td text-sub">{formatDateTime(o.ordered_at)}</td>
                      <td className="td">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge status={o.status} />
                          <PaymentBadge status={o.payment_status} />
                        </div>
                      </td>
                      <td className="td">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/orders/${o.order_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="btn-ghost !px-2.5 !py-1 !text-xs"
                          >
                            View Order
                          </Link>
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-sub">
                            {open ? 'Hide' : 'View'} Formulation
                            <IconChevronDown
                              className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
                            />
                          </span>
                        </div>
                      </td>
                    </tr>,
                    open && (
                      <tr key={`${o.order_id}-detail`} className="bg-edge/10">
                        <td className="px-5 py-4" colSpan={8}>
                          <FormulationIO
                            request={o.request}
                            response={o.response}
                            clusterLabel={o.cluster_label}
                          />
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
          <LoadMore
            shown={rows.length}
            total={total}
            loading={loading}
            onMore={() => setPage((p) => p + 1)}
          />
          <p className="border-t border-edge px-5 py-3 text-xs text-sub">
            Paid, non-cancelled orders containing a made-to-order formula, newest first —
            the same definition the funnel bar counts.
          </p>
        </>
      )}
    </Modal>
  );
}
